// client/src/components/cursos/CursoForm.js
import React, { useState, useEffect, useCallback } from 'react';
import { useMutateCursos } from '../../hooks/useCursos';
import { useCategoriasCurso } from '../../hooks/useCategorias';
import { useMyAccountDetails, useTenantI18n } from '../../hooks/useAccounts';
import { getCurrencySymbol } from '../../utils/formatters';
import useAlert from '../../hooks/useAlert';
import { XCircle, GraduationCap, UploadCloud, Trash2, Loader2, Image as ImageIcon } from 'lucide-react';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';
import CurriculoBuilder from './CurriculoBuilder';
import GerarImagemIABox from '../ai/GerarImagemIABox';

const STEP_LABELS = ['Informações Principais', 'Imagem', 'Conteúdo', 'Configurações'];

const initialState = {
    nome: '',
    descricao: '',
    categoria: '',
    valor: 0,
    cargaHorariaEmMinutos: 0,
    tags: [],
    ativo: true,
    visivelParaCliente: true
};

const CursoForm = ({ onCloseForm, onSaveSuccess, current }) => {
    const { setAlert } = useAlert();
    const { data: accountData } = useMyAccountDetails();
    const accountId = accountData?._id;
    // ✅ ACHADO: rótulo fixo em "(R$)", sem ler configuracoesGerais.i18n
    // da conta.
    const { currency, locale } = useTenantI18n();
    const currencySymbol = getCurrencySymbol(currency, locale);

    const { createCurso, updateCurso, uploadCursoImagem, deleteCursoImagem } = useMutateCursos();
    const { data: categorias, isLoading: loadingCategorias } = useCategoriasCurso(accountId);

    const [formData, setFormData] = useState(initialState);
    const [currentStep, setCurrentStep] = useState(1);
    const [cursoAtual, setCursoAtual] = useState(current);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const isEditing = !!current;
    const loading = createCurso.isPending || updateCurso.isPending;
    const imageLoading = uploadCursoImagem.isPending || deleteCursoImagem.isPending;

    useEffect(() => {
        if (isEditing && current) {
            setFormData({
                nome: current.nome || '',
                descricao: current.descricao || '',
                categoria: current.categoria?._id || current.categoria || '',
                valor: current.valor || 0,
                cargaHorariaEmMinutos: current.cargaHorariaEmMinutos || 0,
                tags: current.tags || [],
                ativo: current.ativo !== undefined ? current.ativo : true,
                visivelParaCliente: current.visivelParaCliente !== undefined ? current.visivelParaCliente : true
            });
            setCursoAtual(current);
        } else {
            setFormData(initialState);
            setCursoAtual(null);
        }
    }, [current, isEditing]);

    const handleImageFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleImageUpload = () => {
        if (!imageFile || !cursoAtual?.id) return;

        uploadCursoImagem.mutate({ cursoId: cursoAtual.id, file: imageFile }, {
            onSuccess: (response) => {
                setImageFile(null);
                setImagePreview(null);
                const input = document.getElementById('curso-image-upload');
                if (input) input.value = '';
                setCursoAtual(response.data.data);
            }
        });
    };

    const handleImageDelete = (imageId) => {
        if (!cursoAtual?.id) return;
        if (window.confirm('Tem certeza que deseja remover esta imagem?')) {
            deleteCursoImagem.mutate({ cursoId: cursoAtual.id, imageId }, {
                onSuccess: (response) => {
                    setCursoAtual(response.data.data);
                }
            });
        }
    };

    const onChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    const onSubmit = (e) => {
        e.preventDefault();

        if (!formData.nome.trim()) {
            return setAlert('O nome do curso é obrigatório.', 'warning');
        }

        const dataToSubmit = {
            nome: formData.nome.trim(),
            descricao: formData.descricao.trim(),
            categoria: formData.categoria || undefined,
            valor: Number(formData.valor) || 0,
            cargaHorariaEmMinutos: Number(formData.cargaHorariaEmMinutos) || 0,
            tags: formData.tags,
            ativo: formData.ativo,
            visivelParaCliente: formData.visivelParaCliente
        };

        if (isEditing) {
            updateCurso.mutate({ id: current.id, data: dataToSubmit }, { onSuccess: onSaveSuccess });
        } else {
            createCurso.mutate(dataToSubmit, { onSuccess: onSaveSuccess });
        }
    };

    return (
        <form onSubmit={onSubmit} noValidate>
            <div className="form-header">
                <h3 className="form-title">
                    <GraduationCap className="icon-title" />
                    {isEditing ? 'Editar Curso' : 'Novo Curso'}
                </h3>
                <button type="button" onClick={onCloseForm} title="Fechar Formulário" className="form-close-button">
                    <XCircle size={24} />
                </button>
            </div>

            <FormStepper currentStep={currentStep} steps={STEP_LABELS} />

            {currentStep === 1 && (
            <div className="form-section">
                <h4 className="form-section-title">Informações Principais</h4>
                <div className="form-grid-container-cols-2">
                    <div className="form-group">
                        <label htmlFor="nome" className="required">Nome do Curso</label>
                        <input
                            type="text"
                            name="nome"
                            id="nome"
                            value={formData.nome}
                            onChange={onChange}
                            required
                            className="form-input"
                            placeholder="Digite o nome do curso"
                            maxLength="150"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="categoria">Categoria</label>
                        <select
                            name="categoria"
                            id="categoria"
                            value={formData.categoria}
                            onChange={onChange}
                            className="form-input"
                            disabled={loadingCategorias}
                        >
                            <option value="">Sem categoria</option>
                            {(categorias?.data || []).map(categoria => (
                                <option key={categoria._id} value={categoria._id}>
                                    {categoria.nome}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group col-span-2">
                        <label htmlFor="descricao">Descrição</label>
                        <textarea
                            name="descricao"
                            id="descricao"
                            value={formData.descricao}
                            onChange={onChange}
                            className="form-input"
                            rows="3"
                            placeholder="Descrição opcional do curso"
                            maxLength="1000"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="valor">Valor ({currencySymbol})</label>
                        <input
                            type="number"
                            name="valor"
                            id="valor"
                            value={formData.valor}
                            onChange={onChange}
                            min="0"
                            step="0.01"
                            className="form-input"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="cargaHorariaEmMinutos">Carga Horária (minutos)</label>
                        <input
                            type="number"
                            name="cargaHorariaEmMinutos"
                            id="cargaHorariaEmMinutos"
                            value={formData.cargaHorariaEmMinutos}
                            onChange={onChange}
                            min="0"
                            step="1"
                            className="form-input"
                        />
                    </div>

                    <div className="form-group col-span-2">
                        <label htmlFor="tags">Tags (opcional)</label>
                        <input
                            type="text"
                            name="tags"
                            id="tags"
                            value={formData.tags.join(', ')}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                            }))}
                            className="form-input"
                            placeholder="tag1, tag2, tag3"
                        />
                    </div>
                </div>
            </div>
            )}

            {currentStep === 2 && (
            <div className="form-section">
                <h4 className="form-section-title">Imagem do Curso</h4>

                {!isEditing || !cursoAtual?.id ? (
                    <div className="panel-state-wrapper panel-info">
                        <ImageIcon size={24} className="icon" />
                        <span>Salve o curso primeiro. Após salvar, você poderá enviar uma imagem aqui.</span>
                    </div>
                ) : (
                    <fieldset disabled={imageLoading} className="border-0 p-0 m-0">
                        {cursoAtual.imagemUrl ? (
                            <div className="flex items-center gap-4 flex-wrap">
                                <img
                                    src={cursoAtual.imagemUrl}
                                    alt={cursoAtual.nome}
                                    className="w-24 h-24 rounded-lg object-cover border shadow-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleImageDelete(cursoAtual.imagemPrincipal?.refId || cursoAtual.imagens?.[0]?._id)}
                                    disabled={imageLoading}
                                    className="action-button action-button-danger"
                                >
                                    {deleteCursoImagem.isPending ? <Loader2 size={16} className="animate-spin icon" /> : <Trash2 size={16} className="icon" />}
                                    Remover Imagem
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-4 flex-wrap">
                                <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                                    {imagePreview
                                        ? <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" />
                                        : <ImageIcon size={32} className="text-gray-400" />}
                                </div>
                                <div className="form-group flex-1 min-w-[250px]">
                                    <label htmlFor="curso-image-upload">Escolher arquivo</label>
                                    <input
                                        type="file"
                                        id="curso-image-upload"
                                        onChange={handleImageFileChange}
                                        accept="image/*"
                                        className="form-input"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleImageUpload}
                                    disabled={!imageFile || imageLoading}
                                    className="action-button action-button-primary"
                                >
                                    {uploadCursoImagem.isPending ? <Loader2 size={16} className="animate-spin icon" /> : <UploadCloud size={16} className="icon" />}
                                    Enviar Imagem
                                </button>
                                <GerarImagemIABox
                                    aspectRatio="3:2"
                                    aspectHint="Tamanho ideal: 800×600px, paisagem. Evite fotos na vertical."
                                    placeholder='Ex: "foto de curso online, pessoa estudando em ambiente moderno"'
                                    onUseImage={(file, previewUrl) => { setImageFile(file); setImagePreview(previewUrl); }}
                                    itemContexto={{
                                        tipo: 'curso',
                                        nome: formData.nome,
                                        descricao: formData.descricao,
                                        categoria: categorias?.find(c => c._id === formData.categoria)?.nome
                                    }}
                                />
                            </div>
                        )}
                    </fieldset>
                )}
            </div>
            )}

            {currentStep === 3 && (
            <div className="form-section">
                <h4 className="form-section-title">Régua de Conteúdo</h4>
                <CurriculoBuilder cursoId={cursoAtual?.id} modulosIniciais={cursoAtual?.modulos} />
            </div>
            )}

            {currentStep === 4 && (
            <div className="form-section">
                <h4 className="form-section-title">Configurações</h4>
                <div className="form-grid-container-cols-2">
                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="ativo"
                                checked={formData.ativo}
                                onChange={onChange}
                                className="checkbox-input"
                            />
                            <span className="checkbox-custom"></span>
                            Curso Ativo
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="visivelParaCliente"
                                checked={formData.visivelParaCliente}
                                onChange={onChange}
                                className="checkbox-input"
                            />
                            <span className="checkbox-custom"></span>
                            Visível para Clientes
                        </label>
                    </div>
                </div>
            </div>
            )}

            <div className="form-actions">
                <button type="button" onClick={onCloseForm} className="action-button action-button-secondary" disabled={loading}>
                    <XCircle size={18} />
                    Cancelar
                </button>
                <FormStepNavigation
                    currentStep={currentStep}
                    totalSteps={STEP_LABELS.length}
                    onBack={() => setCurrentStep((s) => s - 1)}
                    onNext={() => setCurrentStep((s) => s + 1)}
                    isSubmitting={loading}
                    submitLabel={isEditing ? 'Salvar Alterações' : 'Criar Curso'}
                />
            </div>
        </form>
    );
};

export default CursoForm;
